import "../App.css";
import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import {
  TextField,
  Select,
  MenuItem,
  InputLabel,
  Button,
  Grid,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  FormControl,
} from "@mui/material";
import PasswordField from "../PasswordField";
import { Alert, Spinner } from "react-bootstrap";
import * as Yup from "yup";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { CountryDropdown, RegionDropdown } from "react-country-region-selector";
import countryToCurrency from "country-to-currency";
import axios from "axios";
import {
  API_BASE_URL,
  DISABLE_CAPTCHA,
  LOCAL_CAPTCHA_TOKEN,
} from "../../config/config";
import CaptchaField from "../CaptchaField";
import { Link, useNavigate } from "react-router-dom";

const TravelRegistration = () => {
  const [currencies, setCurrencies] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedSelfie, setSelectedSelfie] = useState(null);
  const [recaptchaToken, setRecaptchatToken] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const currencies = Object.values(countryToCurrency);
    const currency = [...new Set(currencies)];
    setCurrencies(currency);
  }, []);

  useEffect(() => {
    const currencies = Object.values(countryToCurrency);
    const currency = [...new Set(currencies)];
    setCurrencies(currency);
  }, []);

  const handleSelfieChange = (event) => {
    const file = event.target.files[0];
    setSelectedSelfie(file);
  };

  const calculateCareValues = (values, index) => {
    let careFees = 0;
    const careAmount = parseFloat(values.services[index].careValueAmount);

    let careExpireTimeline = values.services[index].travelTimeline;

    switch (values.services[index].category) {
      case "Domestic Trip":
        careFees = careAmount * 0.004;
        break;
      case "Hotel Stays":
        careFees = careAmount * 0.003;
        break;
      case "Local Cab Ride":
        careFees = careAmount * 0.005;
        break;
      default:
        break;
    }

    const numberOfFamilyMembers = values.services[index].familyMembers.length;
    careFees = careFees * numberOfFamilyMembers;

    values.services[index].careFees = careFees.toFixed(2);
    values.services[index].careExpireTimeline = careExpireTimeline;
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const formData = new FormData();

      values.services.forEach((service, index) => {
        calculateCareValues(values, index);

        formData.append("selfie", selectedSelfie);

        service.familyMembers.forEach((member, memberIndex) => {
          Object.entries(member).forEach(([memberKey, memberValue]) => {
            formData.append(
              `services[${index}].familyMembers[${memberIndex}][${memberKey}]`,
              memberValue,
            );
          });
        });

        Object.entries(service).forEach(([key, value]) => {
          if (key !== "invoice" && key !== "familyMembers") {
            formData.append(`services[${index}][${key}]`, value);
          }
        });
      });

      Object.keys(values).forEach((key) => {
        if (key !== "services") {
          formData.append(key, values[key]);
        }
      });

      formData.append(
        "recaptchaToken",
        DISABLE_CAPTCHA ? LOCAL_CAPTCHA_TOKEN : recaptchaToken,
      );

      console.log(values);

      await axios.post(`${API_BASE_URL}/register`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setSuccess(true);

      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error) {
      console.error("Error:", error.response?.data?.error || error.message);
      setError("Error submitting the form");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleCaptchaChange = (token) => {
    if (token) {
      setRecaptchatToken(token);
    }
  };

  return (
    <div>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DemoContainer components={["DatePicker"]}>
          <Formik
            initialValues={{
              services: [
                {
                  type: "Travel",
                  category: "",
                  itinerary: "",
                  careFor: "",
                  travelTimeline: "",
                  selfie: "",
                  familyMembers: [],
                  referralCode: "",
                  currency: "",
                  careFees: 0,
                  careValueAmount: 0,
                  careExpireTimeline: "",
                },
              ],
              password: "",
              email: "",
              fName: "",
              mName: "",
              lName: "",
              country: "",
              city: "",
              state: "",
              zip: "",
              contact: "",
            }}
            validationSchema={Yup.object({
              password: Yup.string()
                .required("Password is required")
                .matches(
                  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                  "Password must contain at least 1 uppercase letter, 1 lowercase letter, 1 digit, 1 special character, and be at least 8 characters long",
                ),
              email: Yup.string()
                .email("Invalid email address")
                .required("Email is required"),
              fName: Yup.string().required("First Name is required"),
              lName: Yup.string().required("Last Name is required"),
              country: Yup.string().required("Country is required"),
              city: Yup.string(),
              state: Yup.string(),
              zip: Yup.string()
                .required("Zip is required")
                .matches(/^\d+$/, "Zip must only contain digits")
                .min(5, "Zip must be at least 5 digits")
                .max(10, "Zip cannot exceed 10 digits"),
              contact: Yup.string()
                .required("Contact is required")
                .matches(/^\d+$/, "Contact must only contain digits")
                .min(7, "Contact must be at least 7 digits")
                .max(15, "Contact cannot exceed 15 digits"),
            })}
            onSubmit={handleSubmit}
          >
            {(formik) => (
              <Form noValidate>
                {formik.submitCount > 0 &&
                  Object.keys(formik.errors).length > 0 && (
                    <Alert variant="warning" style={{ marginBottom: "1rem" }}>
                      Please correct the highlighted errors before submitting.
                    </Alert>
                  )}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <InputLabel htmlFor="email" id="emailLabel">
                      Email
                    </InputLabel>
                    <Field
                      as={TextField}
                      style={{ width: "100%" }}
                      size="small"
                      name="email"
                      id="email"
                    />
                    <ErrorMessage name="email" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <InputLabel htmlFor="password" id="passwordLabel">
                      Set Your Password
                    </InputLabel>
                    <Field
                      name="password"
                      id="password"
                      component={PasswordField}
                      style={{ width: "100%" }}
                      size="small"
                    />
                    <ErrorMessage name="password" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="fName" id="fNameLabel">
                      First Name
                    </InputLabel>
                    <Field
                      as={TextField}
                      style={{ width: "100%" }}
                      size="small"
                      name="fName"
                      id="fName"
                    />
                    <ErrorMessage name="fName" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="mName" id="mNameLabel">
                      Middle Name
                    </InputLabel>
                    <Field
                      as={TextField}
                      style={{ width: "100%" }}
                      size="small"
                      name="mName"
                      id="mName"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="lName" id="lNameLabel">
                      Last Name
                    </InputLabel>
                    <Field
                      as={TextField}
                      style={{ width: "100%" }}
                      size="small"
                      name="lName"
                      id="lName"
                    />
                    <ErrorMessage name="lName" />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <InputLabel htmlFor="country" id="countryLabel">
                      Select Country
                    </InputLabel>
                    <CountryDropdown
                      value={formik.values.country}
                      onChange={(val) => formik.setFieldValue("country", val)}
                      className="form-control"
                      name="country"
                      id="country"
                    />
                    <ErrorMessage name="country" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="state" id="stateLabel">
                      State
                    </InputLabel>
                    <Field
                      as={RegionDropdown}
                      country={formik.values.country}
                      value={formik.values.state}
                      onChange={(val) => formik.setFieldValue("state", val)}
                      className="form-control"
                      name="state"
                      id="state"
                    />
                    <ErrorMessage name="state" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="city" id="cityLabel">
                      City
                    </InputLabel>
                    <Field
                      as={TextField}
                      style={{ width: "100%" }}
                      size="small"
                      name="city"
                      id="city"
                    />
                    <ErrorMessage name="city" />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InputLabel htmlFor="zip" id="zipLabel">
                      Zip Code
                    </InputLabel>
                    <Field
                      as={TextField}
                      style={{ width: "100%" }}
                      size="small"
                      name="zip"
                      id="zip"
                    />
                    <ErrorMessage name="zip" />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <InputLabel htmlFor="contact" id="contactLabel">
                      Contact
                    </InputLabel>
                    <Field
                      as={TextField}
                      style={{ width: "100%" }}
                      size="small"
                      name="contact"
                      id="contact"
                    />
                    <ErrorMessage name="contact" />
                  </Grid>
                </Grid>
                {formik.values.services.map((service, index) => (
                  <div key={index}>
                    <Grid container spacing={2} style={{ marginTop: "0" }}>
                      <Grid item xs={12} sm={6}>
                        <InputLabel htmlFor={`services[${index}].category`}>
                          Select Product Category
                        </InputLabel>
                        <Field
                          as={Select}
                          style={{ width: "100%" }}
                          size="small"
                          name={`services[${index}].category`}
                          id={`services[${index}].category`}
                        >
                          <MenuItem value="Domestic Trip">
                            Domestic Trip
                          </MenuItem>
                          <MenuItem value="Hotel Stays">Hotel Stays</MenuItem>
                          <MenuItem value="Local Cab Ride">
                            Local Cab Ride
                          </MenuItem>
                        </Field>
                        <ErrorMessage name={`services[${index}].category`} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel htmlFor={`services[${index}].itinerary`}>
                          Itinerary (Travel Plans)
                        </InputLabel>
                        <Field
                          as={TextField}
                          style={{ width: "100%" }}
                          size="small"
                          name={`services[${index}].itinerary`}
                          id={`services[${index}].itinerary`}
                        />
                        <ErrorMessage name={`services[${index}].itinerary`} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel htmlFor={`services[${index}].careFor`}>
                          Care Value For
                        </InputLabel>
                        <div>
                          <FormControl component="fieldset">
                            <Field name={`services[${index}].careFor`}>
                              {({ field }) => (
                                <>
                                  <RadioGroup {...field} aria-label="careFor">
                                    <FormControlLabel
                                      value="Self"
                                      control={<Radio />}
                                      label="Self"
                                    />
                                    <FormControlLabel
                                      value="Family"
                                      control={<Radio />}
                                      label="Family"
                                    />
                                    <FormControlLabel
                                      value="Everyone"
                                      control={<Radio />}
                                      label="Everyone"
                                    />
                                  </RadioGroup>
                                </>
                              )}
                            </Field>
                          </FormControl>
                        </div>
                        <ErrorMessage name={`services[${index}].careFor`} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel htmlFor={`services[${index}].selfie`}>
                          Selfie
                        </InputLabel>
                        <input
                          style={{ width: "100%" }}
                          size="small"
                          type="file"
                          name={`services[${index}].selfie`}
                          id={`services[${index}].selfie`}
                          onChange={handleSelfieChange}
                        />
                        <ErrorMessage name={`services[${index}].selfie`} />
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <InputLabel>Travel Timeline</InputLabel>
                        <FormControl component="fieldset">
                          <Field name={`services[${index}].travelTimeline`}>
                            {({ field }) => (
                              <>
                                <FormControlLabel
                                  control={<Radio />}
                                  label="1 Hour"
                                  value="1 Hour"
                                  {...field}
                                  checked={field.value === "1 Hour"}
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `services[${index}].travelTimeline`,
                                      "1 Hour",
                                    )
                                  }
                                />
                                <FormControlLabel
                                  control={<Radio />}
                                  label="3 Hours"
                                  value="3 Hours"
                                  {...field}
                                  checked={field.value === "3 Hours"}
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `services[${index}].travelTimeline`,
                                      "3 Hours",
                                    )
                                  }
                                />
                                <FormControlLabel
                                  control={<Radio />}
                                  label="6 Hours"
                                  value="6 Hours"
                                  {...field}
                                  checked={field.value === "6 Hours"}
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `services[${index}].travelTimeline`,
                                      "6 Hours",
                                    )
                                  }
                                />
                                <FormControlLabel
                                  control={<Radio />}
                                  label="12 Hours"
                                  value="12 Hours"
                                  {...field}
                                  checked={field.value === "12 Hours"}
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `services[${index}].travelTimeline`,
                                      "12 Hours",
                                    )
                                  }
                                />
                                <FormControlLabel
                                  control={<Radio />}
                                  label="24 Hours"
                                  value="24 Hours"
                                  {...field}
                                  checked={field.value === "24 Hours"}
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `services[${index}].travelTimeline`,
                                      "24 Hours",
                                    )
                                  }
                                />
                                <FormControlLabel
                                  control={<Radio />}
                                  label="2 Days"
                                  value="2 Days"
                                  {...field}
                                  checked={field.value === "2 Days"}
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `services[${index}].travelTimeline`,
                                      "2 Days",
                                    )
                                  }
                                />
                                <FormControlLabel
                                  control={<Radio />}
                                  label="Till 6 Months"
                                  value="Till 6 Months"
                                  {...field}
                                  checked={field.value === "Till 6 Months"}
                                  onChange={() =>
                                    formik.setFieldValue(
                                      `services[${index}].travelTimeline`,
                                      "Till 6 Months",
                                    )
                                  }
                                />
                              </>
                            )}
                          </Field>
                          <ErrorMessage
                            name={`services[${index}].travelTimeline`}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <Typography variant="h6">Family Members</Typography>
                      </Grid>
                      {service.familyMembers.map((member, memberIndex) => (
                        <React.Fragment key={memberIndex}>
                          <Grid item xs={12} sm={3}>
                            <InputLabel
                              htmlFor={`services[${index}].familyMembers[${memberIndex}].name`}
                            >
                              Name
                            </InputLabel>
                            <Field
                              as={TextField}
                              style={{ width: "100%" }}
                              size="small"
                              name={`services[${index}].familyMembers[${memberIndex}].name`}
                              id={`services[${index}].familyMembers[${memberIndex}].name`}
                            />
                            <ErrorMessage
                              name={`services[${index}].familyMembers[${memberIndex}].name`}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <InputLabel
                              htmlFor={`services[${index}].familyMembers[${memberIndex}].age`}
                            >
                              Age
                            </InputLabel>
                            <Field
                              as={TextField}
                              style={{ width: "100%" }}
                              size="small"
                              name={`services[${index}].familyMembers[${memberIndex}].age`}
                              id={`services[${index}].familyMembers[${memberIndex}].age`}
                            />
                            <ErrorMessage
                              name={`services[${index}].familyMembers[${memberIndex}].age`}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <InputLabel
                              htmlFor={`services[${index}].familyMembers[${memberIndex}].sex`}
                            >
                              Sex
                            </InputLabel>
                            <Field
                              as={Select}
                              style={{ width: "100%" }}
                              size="small"
                              name={`services[${index}].familyMembers[${memberIndex}].sex`}
                              id={`services[${index}].familyMembers[${memberIndex}].sex`}
                            >
                              <MenuItem value="Male">Male</MenuItem>
                              <MenuItem value="Female">Female</MenuItem>
                              <MenuItem value="Other">Other</MenuItem>
                            </Field>
                            <ErrorMessage
                              name={`services[${index}].familyMembers[${memberIndex}].sex`}
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <InputLabel
                              htmlFor={`services[${index}].familyMembers[${memberIndex}].nomineeDetails`}
                            >
                              Nominee Details
                            </InputLabel>
                            <Field
                              as={TextField}
                              style={{ width: "100%" }}
                              size="small"
                              name={`services[${index}].familyMembers[${memberIndex}].nomineeDetails`}
                              id={`services[${index}].familyMembers[${memberIndex}].nomineeDetails`}
                            />
                            <ErrorMessage
                              name={`services[${index}].familyMembers[${memberIndex}].nomineeDetails`}
                            />
                          </Grid>
                        </React.Fragment>
                      ))}
                      <Grid item xs={12}>
                        <Button
                          type="button"
                          variant="contained"
                          color="success"
                          onClick={() =>
                            formik.setFieldValue(`services[0].familyMembers`, [
                              ...formik.values.services[0].familyMembers,
                              {
                                name: "",
                                age: "",
                                sex: "",
                                nomineeDetails: "",
                              },
                            ])
                          }
                        >
                          Add Family Member
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel>Select Currency</InputLabel>
                        <Field
                          as={Select}
                          style={{ width: "100%" }}
                          size="small"
                          name={`services[${index}].currency`}
                          id={`services[${index}].currency`}
                        >
                          <MenuItem value="">Select Currency</MenuItem>
                          {currencies.map((currency) => (
                            <MenuItem key={currency} value={currency}>
                              {currency}
                            </MenuItem>
                          ))}
                        </Field>
                        <ErrorMessage name={`services[${index}].currency`} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel htmlFor={`services[${index}].referralCode`}>
                          Referral Code (If Any)
                        </InputLabel>
                        <Field
                          as={TextField}
                          style={{ width: "100%" }}
                          size="small"
                          name={`services[${index}].referralCode`}
                          id={`services[${index}].referralCode`}
                        />
                        <ErrorMessage
                          name={`services[${index}].referralCode`}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputLabel
                          htmlFor={`services[${index}].careValueAmount`}
                        >
                          Enter Care Amount
                        </InputLabel>
                        <Field
                          as={TextField}
                          style={{ width: "100%" }}
                          size="small"
                          name={`services[${index}].careValueAmount`}
                          id={`services[${index}].careValueAmount`}
                        />
                        <ErrorMessage
                          name={`services[${index}].careValueAmount`}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <InputLabel
                          htmlFor={`services[${index}].careFees`}
                          align="center"
                        >
                          Care Fees to Pay
                        </InputLabel>
                        <Typography
                          variant="h6"
                          align="center"
                          name={`services[${index}].careFees`}
                        >
                          {service.category === "Domestic Trip" &&
                            `${(service.careValueAmount * 0.004).toFixed(2)}`}
                          {service.category === "Hotel Stays" &&
                            `${(service.careValueAmount * 0.003).toFixed(2)}`}
                          {service.category === "Local Cab Ride" &&
                            `${(service.careValueAmount * 0.005).toFixed(2)}`}
                          {service.currency && " " + service.currency}
                        </Typography>
                      </Grid>
                    </Grid>
                  </div>
                ))}
                <CaptchaField
                  onChange={handleCaptchaChange}
                  wrapperStyle={{ display: "flex" }}
                  style={{
                    margin: "0 auto",
                    textAlign: "center",
                    marginTop: "2rem",
                  }}
                />
                <div className="submission-popup">
                  <div className="loading">
                    {loading && <Spinner animation="border" />}
                  </div>
                  {error && <Alert variant="danger">{error}</Alert>}
                  {success && (
                    <Alert variant="success">
                      Form submitted successfully!
                    </Alert>
                  )}
                </div>
                <Grid container spacing={2} justifyContent="center">
                  <Grid item>
                    <Button
                      type="submit"
                      variant="contained"
                      color="success"
                      disabled={formik.isSubmitting || loading}
                      style={{ marginTop: "1rem" }}
                    >
                      {formik.isSubmitting || loading
                        ? "Submitting..."
                        : "Register"}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </DemoContainer>
      </LocalizationProvider>
      <div style={{ marginTop: "1rem", textAlign: "center" }}>
        <Typography variant="body1">
          Already a user? <Link to="/login">Login</Link>
        </Typography>
      </div>
    </div>
  );
};

export default TravelRegistration;
